/**
 * 앱인토스 WebView SDK 어댑터.
 *
 * SDK 호출을 여기 한 곳에 모은다 — 화면 코드가 `@apps-in-toss/web-framework`를
 * 직접 부르기 시작하면 웹 빌드에서 떼어낼 수 없게 된다.
 *
 * **웹 빌드에는 실리지 않는다.** 동적 import라 별도 청크로 갈라지고, `isToss`가 아닌
 * 표면에서는 이 함수 자체가 불리지 않는다.
 */

import { isToss } from './platform';

/** 실제 SDK 모듈 타입 — 손으로 흉내 내지 않고 패키지 타입을 그대로 쓴다 */
type TossSdk = typeof import('@apps-in-toss/web-framework');

/**
 * 미니앱 이름 — 실행 오리진(`tarotwith.web.tossmini.com`)과 딥링크 스킴이 여기서 나온다.
 * 앱인토스 콘솔에 등록된 값과 반드시 같아야 한다 (apps-in-toss.config.ts가 이 값을 쓴다).
 */
export const TOSS_APP_NAME = 'tarotwith';

let cached: TossSdk | null | undefined;

/**
 * SDK 로드 (1회 캐시). 사용할 수 없으면 `null` — 호출부는 반드시 이 경우를 다뤄야 한다.
 * (토스 앱 밖에서 열렸거나, 번들이 미니앱으로 배포되지 않은 상태)
 */
export async function loadTossSdk(): Promise<TossSdk | null> {
  if (cached !== undefined) return cached;
  try {
    cached = await import('@apps-in-toss/web-framework');
  } catch {
    cached = null;
  }
  return cached;
}

/**
 * 웹 주소 → 미니앱 딥링크.
 *
 * ⚠️ `Share.createLink`는 **`intoss://`로 시작하는 딥링크만** 받는다. 웹 주소를 그대로 넘기면
 * 받는 사람이 토스 밖 브라우저에서 열어 방이 갈린다 — 초대 동선이 깨지는 지점이다.
 * 경로는 콘솔 **주요 기능** 탭에 등록한 것과 같아야 한다.
 */
export function tossDeepLink(url: string): string {
  let path = '/';
  try {
    const u = new URL(url, location.href);
    path = `${u.pathname}${u.search}`;
  } catch {
    /* 경로를 못 읽으면 홈으로 — 링크가 죽는 것보다 낫다 */
  }
  return `intoss://${TOSS_APP_NAME}${path}`;
}

/**
 * 웹 주소 → **토스 밖에서 누를 수 있는** 미니앱 진입 주소. 웹은 토스 전용이라 모바일 웹 방문자를
 * 여기로 보낸다 (components/TossOnlyGate.tsx).
 *
 * `entryLink`(토스 공유 링크, 서버 설정 `tossEntryLink`)가 있으면 **그 링크를 그대로** 쓴다.
 * 토스가 있으면 미니앱이, 없으면 앱스토어·플레이스토어가 열린다.
 *
 * ⚠️ 이 링크는 목적지가 고정이다. `https://minion.toss.im/…` 짧은 링크는 뒤에 `deep_link_value`를
 * 붙여도 무시하고 처음 만든 화면(우리 것은 미니앱 홈)으로 보낸다 — 붙인 것과 안 붙인 것의
 * 리다이렉트가 글자 하나 다르지 않았다 (2026-10-10 실측). 그래서 지금 경로는 살리지 못한다.
 *
 * 없으면 지금 경로를 살린 `intoss://` 딥링크 — 토스가 깔린 기기에서만 열린다.
 */
export function tossEntryUrl(url: string, entryLink: string): string {
  // 설정값이 https 주소가 아니면 쓰지 않는다 — 엉뚱한 값으로 사람을 보내느니 열리는 쪽으로 물러난다
  return /^https:\/\//.test(entryLink) ? entryLink : tossDeepLink(url);
}

/**
 * 콘솔 '핵심 지표'에 묶을 이벤트 이름 — **여기가 단일 출처**.
 *
 * 토스는 활성·전환 지표를 기준으로 노출·추천을 최적화한다. 우리가 이벤트를 보내지 않으면
 * 콘솔에는 토스가 자동으로 심는 방문·체류 이벤트만 남아서, "해석 완료" 같은 전환을
 * 정의할 방법이 아예 없다.
 *
 * ⚠️ 이름을 바꾸면 콘솔에 등록해 둔 지표가 **조용히 0이 된다**. 새 이벤트는 추가하되
 * 기존 이름은 바꾸지 말 것. 이벤트가 콘솔 목록에 뜨려면 번들 배포 후 실제 발생이 필요하다.
 */
export const TOSS_EVENTS = {
  /** 해석이 끝까지 생성됨 — 대표 전환 */
  readingComplete: 'reading_complete',
  /** 방에 자리를 받음(생성·참여 공통) — 친구를 데려오는 행동 */
  roomEnter: 'room_enter',
  /** 리워드 광고를 끝까지 봄 — 수익 발생 */
  adComplete: 'ad_complete',
  /** 공유를 실제로 보냄 — 바이럴 유입원 */
  shareComplete: 'share_complete',
} as const;

export type TossEvent = (typeof TOSS_EVENTS)[keyof typeof TOSS_EVENTS];

/** `Analytics.log`의 params가 받는 값 (SDK의 LogParam과 같은 범위) */
type LogParam = string | number | boolean | null | undefined;

/**
 * 토스 분석 이벤트 전송. **웹에서는 아무 일도 하지 않는다.**
 *
 * 통계는 부가 기능이라 어떤 실패도 서비스에 영향을 주지 않는다 — 기다리지 않고(void),
 * 던지지도 않는다. 개인정보는 넣지 말 것 (`anonymous_key`는 SDK가 알아서 붙인다).
 */
export function tossLog(name: TossEvent, params: Record<string, LogParam> = {}): void {
  if (!isToss) return;
  void (async () => {
    try {
      const sdk = await loadTossSdk();
      await sdk?.Analytics.log({ log_name: name, log_type: 'event', params });
    } catch {
      /* 분석 실패는 무시 — 화면은 그대로 진행된다 */
    }
  })();
}

/**
 * 이 기기에서 **미니앱 리뷰 창을 띄울 수 있는가.**
 *
 * 웹이거나 구버전 토스 앱(안드로이드·iOS 5.253.0 미만)이면 `false`. 부탁 문구를 띄우기 전에
 * 먼저 물어보는 용도다 — 눌러도 아무 일이 없는 버튼을 보여주지 않기 위해서다.
 */
export async function canRequestTossReview(): Promise<boolean> {
  if (!isToss) return false;
  const sdk = await loadTossSdk();
  if (!sdk) return false;
  try {
    return sdk.Review.request.isSupported();
  } catch {
    return false; // 상수 주입 전이거나 SDK가 바뀐 경우 — 부탁하지 않는다
  }
}

/**
 * 미니앱 리뷰 요청 — **토스가 자기 리뷰 창을 띄운다.** 우리가 별점을 받거나 보관하지 않는다.
 *
 * 반환값은 "리뷰를 썼는가"가 아니라 **창을 띄웠는가**다. 사용자가 실제로 남겼는지는
 * 토스만 알고 우리에게 오지 않는다 — 남겼는지로 보상을 주는 설계를 하면 안 된다.
 *
 * ⚠️ 반드시 **사용자가 누른 뒤에만** 부른다. 해석을 읽는 중에 창이 뜨면 본문을 가린다.
 */
export async function requestTossReview(): Promise<boolean> {
  if (!(await canRequestTossReview())) return false;
  const sdk = await loadTossSdk();
  try {
    await sdk?.Review.request();
    return true;
  } catch {
    return false; // 창을 못 띄웠다 — 호출부가 안내를 남긴다
  }
}
